import React, { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import Landing from "./components/landing";
import Navigation from "./components/navigation";
import Messages from "./components/Messages";
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import "./App.css";
import ShopNow from './components/ShopNow';
import ProfileContextProvider from "./store/profile_context";
import GlobalContextProvider from "./store/global_context";
import SettingsPage from "./components/SettingsPage";
import { ChakraProvider, Box, useColorModeValue } from "@chakra-ui/react";
import ProfileDetails from "./components/ProfileDetails";
import Onboarding from "./components/Onboarding";
import ProtectedRoute from "./components/ProtectedRoutes";
import AccountDisabled from "./components/AccountDisabled";
import AuthContextProvider from "./store/auth-context";
import { MessagesProvider } from "./store/messages-context";
import GetStarted from "./components/GetStarted";
import Footerx from "./components/Footerx";
import MatchDetails from "./components/MatchDetails";
import TermsGuidelines from "./components/TermsGuidelines";
import theme from "./theme";
import MobileBottomNav from "./components/MobileBottomNav";
import MobileTopBar from "./components/MobileTopBar";
import InstallPrompt from "./components/InstallPrompt";
import PwaUpdateToast from "./components/PwaUpdateToast";
import LikesPage from "./components/LikesPage";
import PushNotificationManager from "./components/PushNotificationManager";
import PushNotificationPrompt from "./components/PushNotificationPrompt";
import PushNotificationToast from "./components/PushNotificationToast";
import { LanguageModalProvider } from "./context/LanguageModalContext";
import { MatchCelebrationProvider } from "./context/MatchCelebrationContext";
import AdminPage from "./components/Admin/AdminPage";
import AdminGate from "./components/Admin/AdminGate";
import { APP_CONTENT_SHELL } from "./util/contentShell";
import { MOBILE_BOTTOM_NAV_TOTAL_HEIGHT, MOBILE_EXPLORE_ACTION_OFFSET } from "./constants/mobileApp";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  const location = useLocation();
  let user = null;
  try {
    const raw = localStorage.getItem("user");
    user = raw ? JSON.parse(raw) : null;
  } catch {
    user = null;
  }

  const isAdminRoute = location.pathname.startsWith("/admin");

  const isGetStartedFlow =
    location.pathname === "/get-started" || location.pathname === "/onboarding";

  const showMobileDock =
    !!user &&
    location.pathname !== "/" &&
    location.pathname !== "/Account-disabled" &&
    location.pathname !== "/terms" &&
    !isAdminRoute;

  const isExploreRoute = location.pathname === "/shop";

  // Get Started hides the bottom dock — don't reserve nav space (only safe area if needed).
  const mobileDockPadding =
    showMobileDock && !isGetStartedFlow
      ? isExploreRoute
        ? MOBILE_EXPLORE_ACTION_OFFSET
        : MOBILE_BOTTOM_NAV_TOTAL_HEIGHT
      : 0;

  const mainBg = useColorModeValue(
    'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 40%, #f8fafc 100%)',
    'linear-gradient(180deg, #0a0a0b 0%, #0d0d10 40%, #0a0a0b 100%)'
  );

  // Scroll to top on route change (app-like screen transitions).
  // Preserve in-page anchors (e.g. /terms#guidelines).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1));
      const timer = window.setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      return () => window.clearTimeout(timer);
    }
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname, location.hash]);

  useEffect(() => {
    const getUserLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            localStorage.setItem("userLocation", JSON.stringify({ lat: latitude, long: longitude }));
          },
          (err) => {
            console.error("Error getting location:", err.message);
          }
        );
      } else {
        console.error("Geolocation is not supported by this browser.");
      }
    };
    getUserLocation();
  }, []);

  return (
    <GlobalContextProvider>
      <AuthContextProvider>
        <ProfileContextProvider>
          <MessagesProvider>
          <MatchCelebrationProvider>
          <div className="App" style={{ minWidth: 0, overflowX: 'hidden' }}>
            <Navigation />
            <MobileTopBar />
            <Box
              minH={{ base: "calc(100vh - 56px)", md: "calc(100vh - 64px)" }}
              display="flex"
              flexDirection="column"
              bg={mainBg}
              pb={{ base: mobileDockPadding, lg: 0 }}
            >
              <Box flex="1" w="100%" minW={0}>
                <Box
                  {...APP_CONTENT_SHELL}
                  px={isGetStartedFlow ? { base: 0, md: 6, lg: 8 } : APP_CONTENT_SHELL.px}
                  py={{ base: showMobileDock || isGetStartedFlow ? 0 : 4, md: 8 }}
                >
                  <Routes>
                    <Route path="/" element={<Landing />} />
                    <Route path="/terms" element={<TermsGuidelines />} />
                    <Route
                      path="/admin"
                      element={
                        <AdminGate>
                          <AdminPage />
                        </AdminGate>
                      }
                    />
                    <Route path="/Account-disabled" element={<AccountDisabled />} />
                    <Route path="/get-started" element={<GetStarted />} />
                    <Route path="/onboarding" element={user ? <ProtectedRoute><Onboarding /></ProtectedRoute> : <Navigate to="/" replace />} />
                    <Route path="/Messages" element={user ? <ProtectedRoute><Messages /></ProtectedRoute> : <Navigate to="/" replace />} />
                    <Route path="/messages/:conversationId" element={user ? <ProtectedRoute><Messages /></ProtectedRoute> : <Navigate to="/" replace />} />
                    <Route path="/shop" element={user ? <ProtectedRoute><ShopNow /></ProtectedRoute> : <Navigate to="/" replace />} />
                    <Route path="/likes" element={user ? <ProtectedRoute><LikesPage /></ProtectedRoute> : <Navigate to="/" replace />} />
                    <Route path="/profile/:id" element={user ? <ProtectedRoute><ProfileDetails /></ProtectedRoute> : <Navigate to="/" replace />} />
                    <Route path="/match/:id" element={user ? <ProtectedRoute><MatchDetails /></ProtectedRoute> : <Navigate to="/" replace />} />
                    <Route path="/like/:id" element={user ? <ProtectedRoute><ProfileDetails /></ProtectedRoute> : <Navigate to="/" replace />} />
                    <Route path="/settings" element={user ? <ProtectedRoute><SettingsPage /></ProtectedRoute> : <Navigate to="/" replace />} />
                  </Routes>
                </Box>
              </Box>
              {!isAdminRoute && <Footerx />}
            </Box>
            <MobileBottomNav />
            <InstallPrompt visible={showMobileDock} />
            <PushNotificationManager />
            <PushNotificationPrompt visible={showMobileDock} />
            <PwaUpdateToast showMobileDock={showMobileDock} />
            <PushNotificationToast />
          </div>
          </MatchCelebrationProvider>
          </MessagesProvider>
        </ProfileContextProvider>
      </AuthContextProvider>
    </GlobalContextProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ChakraProvider theme={theme} resetCSS>
        <LanguageModalProvider>
          <AppRoutes />
        </LanguageModalProvider>
      </ChakraProvider>
    </QueryClientProvider>
  );
}

export default App;
